import { useEffect, useRef, type ReactNode } from "react";

// Generic bottom sheet — backdrop + sheet wrapper, slide-up, paper background,
// closes on backdrop click and Escape. Per spec:
// - Backdrop: 55% ink + 2 px blur
// - Shadow: 0 -24px 60px rgba(43,26,15,0.30)
export default function BottomSheet({
  open,
  onClose,
  children,
  ariaLabel,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  ariaLabel?: string;
}) {
  const sheetRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // simple focus trap: focus the sheet
    sheetRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      aria-hidden={false}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        background: "rgba(43,26,15,0.55)",
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
        animation: "vt-fade-in 160ms ease-out",
      }}
      onClick={onClose}
    >
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--paper-50)",
          borderRadius: "24px 24px 0 0",
          padding: "14px 22px max(34px, env(safe-area-inset-bottom))",
          boxShadow: "0 -24px 60px rgba(43,26,15,0.30)",
          maxHeight: "92vh",
          overflowY: "auto",
          animation: "vt-sheet-up 240ms cubic-bezier(0.2, 0.8, 0.2, 1)",
          width: "100%",
          maxWidth: 480,
          marginInline: "auto",
          outline: "none",
        }}
      >
        {/* Drag handle */}
        <div
          aria-hidden
          style={{
            width: 44,
            height: 4,
            borderRadius: 2,
            background: "rgba(43,26,15,0.18)",
            margin: "0 auto 18px",
          }}
        />
        {children}
      </div>
    </div>
  );
}
